import React, { useCallback } from 'react';
import { message } from 'antd';
import type { DiagramTreeNode } from '../model/diagram-tree-model';
import { useSimulationController } from './hooks/useSimulationController';
import type { SimulationSnapshot, SimulationStatus } from './types';

interface SimulationFeatureRenderProps {
  simulationSnapshot: SimulationSnapshot | null;
  simulationStatus: SimulationStatus;
  onToggleSimulation?: () => void;
  onResetSimulation?: () => void;
}

interface SimulationFeatureProps {
  enabled: boolean;
  currentDiagram: DiagramTreeNode;
  resetKey?: unknown;
  navigateToDiagram?: (diagram: DiagramTreeNode) => Promise<void>;
  onBeforeStart?: () => void;
  children: (props: SimulationFeatureRenderProps) => React.ReactNode;
}

const DISABLED_SIMULATION_PROPS: SimulationFeatureRenderProps = {
  simulationSnapshot: null,
  simulationStatus: 'idle',
  onToggleSimulation: undefined,
  onResetSimulation: undefined,
};

const SimulationFeatureEnabled: React.FC<
  Omit<SimulationFeatureProps, 'enabled'>
> = ({
  currentDiagram,
  resetKey,
  navigateToDiagram,
  onBeforeStart,
  children,
}) => {
  const {
    simulationSnapshot,
    simulationStatus,
    startSimulation,
    stopSimulation,
    resetSimulation,
  } = useSimulationController({
    resetKey,
    navigateToDiagram,
  });

  const handleToggleSimulation = useCallback(async () => {
    if (simulationStatus === 'running') {
      stopSimulation();
      return;
    }

    // 仿真启动前先清掉编辑态 UI，避免属性面板和浮动菜单遮挡执行效果。
    onBeforeStart?.();

    try {
      await startSimulation(currentDiagram);
    } catch (error: any) {
      message.warning(error?.message || '仿真启动失败');
    }
  }, [
    currentDiagram,
    navigateToDiagram,
    onBeforeStart,
    simulationStatus,
    startSimulation,
    stopSimulation,
  ]);

  return (
    <>
      {children({
        simulationSnapshot,
        simulationStatus,
        onToggleSimulation: handleToggleSimulation,
        onResetSimulation: resetSimulation,
      })}
    </>
  );
};

const SimulationFeature: React.FC<SimulationFeatureProps> = ({
  enabled,
  currentDiagram,
  resetKey,
  navigateToDiagram,
  onBeforeStart,
  children,
}) => {
  if (!enabled) {
    return <>{children(DISABLED_SIMULATION_PROPS)}</>;
  }

  return (
    <SimulationFeatureEnabled
      currentDiagram={currentDiagram}
      resetKey={resetKey}
      navigateToDiagram={navigateToDiagram}
      onBeforeStart={onBeforeStart}
    >
      {children}
    </SimulationFeatureEnabled>
  );
};

export default SimulationFeature;
